<script setup lang="ts">
import type { PaymentMethodResponse } from '@@/types/api';

const props = defineProps<{
  modelValue: boolean;
  paymentMethods: PaymentMethodResponse[];
  isSubmitting: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  submit: [paymentMethodId: string];
  'card-added': [];
}>();

const selectedMethodId = ref<string | null>(null);
const showAddCardDialog = ref(false);

const brandLabels: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function brandLabel(brand: string): string {
  return brandLabels[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

// Auto-select when exactly one card exists
watch(
  () => props.paymentMethods,
  (methods) => {
    if (methods.length === 1 && methods[0]) {
      selectedMethodId.value = methods[0].id;
    }
  },
  { immediate: true },
);

function close() {
  emit('update:modelValue', false);
}

function handleSubmit() {
  if (selectedMethodId.value) {
    emit('submit', selectedMethodId.value);
  }
}

function handleCardAdded(_paymentMethodId: string) {
  showAddCardDialog.value = false;
  emit('card-added');
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    :persistent="props.isSubmitting"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-h6">Submit for Review</v-card-title>
      <v-card-text>
        <p class="text-body-2 mb-4">
          Select a payment method to use for this event. This card will be
          charged the platform fee if your event is approved.
        </p>

        <!-- Payment method list -->
        <v-radio-group
          v-if="paymentMethods.length > 0"
          v-model="selectedMethodId"
          class="mb-2"
        >
          <v-radio
            v-for="pm in paymentMethods"
            :key="pm.id"
            :value="pm.id"
            class="mb-1"
          >
            <template #label>
              <div class="d-flex align-center">
                <v-icon size="small" class="mr-2">mdi-credit-card</v-icon>
                <span class="font-weight-medium">{{
                  brandLabel(pm.brand)
                }}</span>
                <span class="text-medium-emphasis ml-2">
                  &bull;&bull;&bull;&bull; {{ pm.last_four }}
                  &middot;
                  {{ pm.exp_month }}/{{ pm.exp_year }}
                </span>
              </div>
            </template>
          </v-radio>
        </v-radio-group>

        <!-- Empty state -->
        <div v-else class="text-center py-4">
          <v-icon size="40" color="on-surface-variant" class="mb-2">
            mdi-credit-card-off-outline
          </v-icon>
          <p class="text-body-2 text-on-surface-variant">
            No saved payment methods. Add a card to continue.
          </p>
        </div>

        <v-btn
          variant="tonal"
          size="small"
          prepend-icon="mdi-plus"
          @click="showAddCardDialog = true"
        >
          Add a new card
        </v-btn>

        <p class="text-body-2 text-medium-emphasis mt-4">
          By submitting your event for review, you agree to the
          <NuxtLink to="/organizer-agreement" target="_blank">
            Organizer Agreement</NuxtLink
          >.
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="isSubmitting" @click="close">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="isSubmitting"
          :disabled="!selectedMethodId"
          @click="handleSubmit"
        >
          Submit for Review
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <ClientOnly>
    <DashboardAddPaymentMethodDialog
      v-model="showAddCardDialog"
      :is-submitting="false"
      @submit="handleCardAdded"
    />
  </ClientOnly>
</template>
